import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import ResponsiveModal from 'presentation/shared/components/ResponsiveModal'
import CidsTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/CidsTable'
import CidsList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/CidsList'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { CidV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { Cid } from 'domain/entities/cid'
import { useServices } from 'presentation/hooks/use-services'
import { ScrollableTableContainer } from '../components/styles'

import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'

type AddCidResponsiveModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (cids: CidV2Props[]) => void
  cids: CidV2Props[]
}

type CidSuggestion = {
  label: string
  value: Cid
}

const AddCidResponsiveModal = ({
  isOpen,
  onClose,
  onConfirm,
  cids
}: AddCidResponsiveModalProps) => {
  const surgicalOrderService = useServices().surgicalOrder
  const [cidSearchTerm, setCidSearchTerm] = useState('')
  const [cidsList, setCidsList] = useState<CidSuggestion[]>([])
  const [selectedCid, setSelectedCid] = useState<Cid | null>(null)
  const [addedCids, setAddedCids] = useState<CidV2Props[]>(cids)

  useEffect(() => {
    if (isOpen) {
      setAddedCids(cids)
    }
  }, [cids, isOpen])

  const resetModalState = () => {
    setCidSearchTerm('')
    setCidsList([])
    setSelectedCid(null)
  }

  const handleClose = () => {
    resetModalState()
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(addedCids)
    resetModalState()
  }

  const getCids = async (searchTerm: string) => {
    try {
      if (searchTerm) {
        const searchCids = await surgicalOrderService.searchCID({
          name: searchTerm
        })

        const suggestionsCids = searchCids.map((cid) => ({
          label: `${cid.code} - ${cid.description}`,
          value: cid
        }))

        setCidsList(suggestionsCids)
      } else {
        setCidsList([])
      }
    } catch (error: any) {
      toast.error('Ocorreu um erro ao buscar CIDs')
      setCidsList([])
    }
  }

  const handleCidSelect = (cid: CidSuggestion) => {
    const cidExists = addedCids.some((c) => c.code === cid.value.code)

    if (cidExists) {
      toast.error('Este CID já está na listagem')
      setCidsList([])
      return
    }

    setSelectedCid(cid.value)
    setCidSearchTerm(cid.label)
    setCidsList([])
  }

  const handleCidInputChange = (value: string) => {
    setCidSearchTerm(value)
    if (!value) {
      setSelectedCid(null)
    }
  }

  const handleAddCid = () => {
    if (!selectedCid) {
      return
    }

    const cidExists = addedCids.some((c) => c.code === selectedCid.code)

    if (cidExists) {
      toast.error('Este CID já foi adicionado')
      return
    }

    const newCid: CidV2Props = {
      code: selectedCid.code,
      description: selectedCid.description,
      is_main: cids.length < 1 && addedCids.length < 1
    }

    setAddedCids((prev) => [...prev, newCid])
    setSelectedCid(null)
    setCidSearchTerm('')
  }

  const handleDeleteCid = (cidCode: string) => {
    setAddedCids((prev) =>
      prev
        .filter((c) => c.code !== cidCode)
        .map((c, index) => {
          return { ...c, is_main: index === 0 }
        })
    )
    toast.success('CID removido com sucesso')
  }

  const isAddButtonDisabled = !selectedCid
  const isConfirmButtonDisabled = addedCids.length === 0

  return (
    <ResponsiveModal
      isOpen={isOpen}
      title="Adicionar CIDs"
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmButtonLabel="Salvar e prosseguir"
      disableConfirmButton={isConfirmButtonDisabled}
    >
      {(isMobile) => (
        <>
          <S.SearchContainer>
            <AutoCompleteNew
              name="cid"
              placeholder="Digite o código ou nome do CID"
              onInputChange={handleCidInputChange}
              returnFullSuggestion
              label="Código ou nome do CID"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelColor="textSecondary"
              labelOverlapping
              iconPosition="insideLeft"
              icon={<SearchIcon />}
              minCharactersSearch={1}
              suggestions={cidsList || []}
              onSuggestionClick={handleCidSelect}
              value={cidSearchTerm}
              onType={getCids}
              debounceDelay={800}
              useClearableInput
              required
              requiredColor="textSecondary"
            />
          </S.SearchContainer>

          <ButtonNew
            outlined
            size="large"
            fullWidth
            onClick={handleAddCid}
            disabled={isAddButtonDisabled}
          >
            Adicionar CID
          </ButtonNew>

          {!!addedCids && addedCids.length > 0 && (
            <>
              <HeadingNew
                as="h2"
                color="textSecondary"
                weight="bold"
                size="xsmall"
                style={{
                  textAlign: 'start',
                  marginTop: '2rem',
                  marginBottom: '0.5rem'
                }}
              >
                CIDs adicionados:
              </HeadingNew>

              <ScrollableTableContainer>
                {isMobile ? (
                  <CidsList
                    initialRows={addedCids}
                    onDelete={handleDeleteCid}
                  />
                ) : (
                  <CidsTable
                    initialRows={addedCids}
                    onDelete={handleDeleteCid}
                  />
                )}
              </ScrollableTableContainer>
            </>
          )}
        </>
      )}
    </ResponsiveModal>
  )
}

export default AddCidResponsiveModal
